import React from 'react';
import {
  AppBar,
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  AccountTree as AccountTreeIcon,
  AdminPanelSettings as PermissionIcon,
  ArrowForward as ArrowForwardIcon,
  AutoAwesome as AgentIcon,
  CheckCircle as CheckIcon,
  Code as ContractIcon,
  DarkMode as DarkModeIcon,
  FactCheck as AuditIcon,
  Hub as HubIcon,
  LightMode as LightModeIcon,
  Lock as LockIcon,
  Policy as PolicyIcon,
  ReceiptLong as ReceiptIcon,
  Security as SecurityIcon,
  SyncAlt as SettlementIcon,
  TravelExplore as TravelIcon,
  VerifiedUser as IdentityIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import CompanyFooter from '@/components/CompanyFooter';
import RtaDexLogo from '@/components/RtaDexLogo';
import { useThemeMode } from '@/theme/ThemeProvider';

const Brand = {
  blue: '#1769E0',
  cyan: '#06B6D4',
  teal: '#0D9488',
  navy: '#123B73',
  green: '#16875B',
  amber: '#B76E00',
};

const navLinks = [
  { label: '系统定位', href: '#positioning' },
  { label: '撮合能力', href: '#capabilities' },
  { label: 'RTA 资产', href: '#rta-lifecycle' },
  { label: '角色系统', href: '#roles' },
  { label: '技术架构', href: '#architecture' },
  { label: '智能协作', href: '#agents' },
  { label: '安全边界', href: '#governance' },
] as const;

const trustSignals = [
  { label: '身份', value: '受控准入' },
  { label: '合约', value: '确定执行' },
  { label: '审计', value: '全程留痕' },
  { label: '智能体', value: '权限治理' },
] as const;

const positioningLayers = [
  {
    code: '01',
    acronym: 'TIBCOS',
    title: '可信协作底座',
    description: '为多机构身份、权限、账本、合约和审计提供联盟链基础。',
    caption: '依靠什么协作',
  },
  {
    code: '02',
    acronym: 'RTA',
    title: '真实文旅权益模型',
    description: '把门票、间夜和体验权益表达为来源清晰、规则明确、可履约的数据对象。',
    caption: '承载什么权益',
  },
  {
    code: '03',
    acronym: 'RtaDex',
    title: '智能合约撮合系统',
    description: '承载文旅权益的准入、挂单、撮合、结算、退款、迁移和审计流程。',
    caption: '如何撮合执行',
  },
] as const;

const capabilities = [
  {
    icon: <IdentityIcon />,
    title: '身份与权限治理',
    description: '统一管理机构、角色、钱包和资源归属边界，UI 隐藏不替代服务端授权。',
  },
  {
    icon: <TravelIcon />,
    title: '文旅权益发行',
    description: '覆盖票种创建、批量发行、来源追溯、状态管理与实名持票模型。',
  },
  {
    icon: <ContractIcon />,
    title: '合约撮合能力',
    description: '支持订单簿、定价合约与零售供给路径，撮合过程受机构准入、资产状态和合约权限约束。',
  },
  {
    icon: <SettlementIcon />,
    title: '原子结算协同',
    description: '保持文旅权益与结算凭证同步演进，支持失败回退、链上回执与持续对账。',
  },
  {
    icon: <ReceiptIcon />,
    title: '退款与迁移管理',
    description: '覆盖退款审批、核销销毁、应急清退和同实名主体地址迁移。',
  },
  {
    icon: <AuditIcon />,
    title: '审计与对账',
    description: '关联调用人、业务状态、交易回执和审计记录，避免单层成功误判。',
  },
] as const;

const workflowSteps = [
  { number: '01', title: '身份准入', description: '建立业务账户并授予最小角色。' },
  { number: '02', title: '权益发行', description: '创建票种并发行文旅权益资产。' },
  { number: '03', title: '合约挂单', description: '按授权路径提交业务订单。' },
  { number: '04', title: '撮合结算', description: '执行合约并同步资产状态。' },
  { number: '05', title: '履约处置', description: '覆盖核销、退款、清退与迁移。' },
  { number: '06', title: '证据对账', description: '核对数据库、事件、回执和审计链。' },
] as const;

const architectureLayers = [
  { label: '体验层', value: '9 个角色子系统', detail: '景区、机构、银行、监管、消费者与运营后台' },
  { label: '服务层', value: '11 个微服务', detail: '领域服务独立持有数据与权限边界' },
  { label: '治理层', value: '身份 · 策略 · 审批 · 审计', detail: '高风险操作需要确认、留痕与可回放证据' },
  { label: '合约层', value: '票务 · 撮合 · 结算 · 迁移', detail: '模块化合约承载确定性业务规则' },
  { label: '可信层', value: 'TIBCOS 联盟链', detail: '机构准入、国密通信、多方确认与可追溯账本' },
] as const;

const agentFlow = [
  { icon: <HubIcon />, title: '统一工具入口', description: '智能体只通过经过治理的 MCP 工具访问业务能力。' },
  { icon: <PermissionIcon />, title: '身份与角色透传', description: '调用身份由平台验证，不能由客户端自行声明。' },
  { icon: <LockIcon />, title: '高风险操作确认', description: '写操作绑定调用人、工具和精确参数，并限制一次执行。' },
  { icon: <AuditIcon />, title: '执行证据归档', description: '记录请求、结果、耗时、风险等级和脱敏参数。' },
] as const;

const governanceItems = [
  {
    icon: <PolicyIcon />,
    title: '资产与凭证边界',
    description: '系统面向经授权的文旅权益资产与结算凭证，不直接提供公众支付、投资理财或资金托管服务。',
  },
  {
    icon: <SecurityIcon />,
    title: '许可型参与边界',
    description: '参与机构、角色和资源范围明确受控，不是匿名、无许可的公链交易系统。',
  },
  {
    icon: <AccountTreeIcon />,
    title: '执行与治理分离',
    description: '业务执行、系统管理、监管审批和链上权限分别约束，避免单一角色越权。',
  },
  {
    icon: <AuditIcon />,
    title: '证据与验收分离',
    description: '构建、部署、服务健康、链上回执和业务 UAT 分别记录，不用单一结果替代完整验收。',
  },
] as const;

const rtaAssetTypes = [
  { code: 'TICKET', title: '景区与活动门票', description: '承载场次、有效期、实名要求、预约容量与核销规则。' },
  { code: 'STAY', title: '酒店间夜与度假权益', description: '表达房型、入住日期、资格条件、取消策略与履约状态。' },
  { code: 'TRAVEL', title: '交通与行程权益', description: '连接里程、接驳、行程服务和跨机构可验证履约凭证。' },
  { code: 'EXPERIENCE', title: '文创与体验权益', description: '覆盖演艺、讲解、文创购买权和可组合的在地体验服务。' },
] as const;

const rtaLifecycle = [
  { number: '01', title: '资源登记', description: '确认发行主体、现实资源、服务承诺与责任边界。' },
  { number: '02', title: '规则定义', description: '固化有效期、实名、价格、流通、退款与核销策略。' },
  { number: '03', title: '可信发行', description: '生成可验证的 RTA 标识、库存和发行记录。' },
  { number: '04', title: '受控撮合', description: '按授权路径完成机构分销、一口价或订单簿撮合。' },
  { number: '05', title: '确定结算', description: '权益、凭证、手续费与分账状态保持一致演进。' },
  { number: '06', title: '现实履约', description: '预约、动态凭证、现场核验、退款和争议形成闭环证据。' },
] as const;

const roleSystems = [
  { name: '景区运营端', scope: '票务发行', features: ['票种与库存', '批量发行', '核销与退款'] },
  { name: '机构撮合端', scope: '机构业务', features: ['订单簿', '一口价', '成交与结算'] },
  { name: '机构管理端', scope: '市场运营', features: ['参数配置', '异常处置', '业务审计'] },
  { name: '做市商端', scope: '流动性供给', features: ['库存管理', '报价策略', '零售供给'] },
  { name: '银行服务端', scope: '凭证管理', features: ['FiatToken', '清分对账', '退款协同'] },
  { name: '监管端', scope: '穿透治理', features: ['主体审查', '风险监测', '证据导出'] },
  { name: '消费者端', scope: '权益使用', features: ['购买持有', '预约核销', '退款申请'] },
  { name: '消费者管理端', scope: '服务运营', features: ['订单支持', '争议处理', '消息触达'] },
  { name: '总管理后台', scope: '平台治理', features: ['角色权限', '服务状态', '全局审计'] },
] as const;

const contractGroups = [
  {
    title: '身份与价值凭证',
    description: '定义谁可以参与、以什么身份操作，以及结算凭证如何受控发行。',
    modules: [
      ['Auth', '角色授权、多签审批与治理级暂停'],
      ['Account', '账户注册、状态控制与同主体标识'],
      ['FiatToken', '结算凭证发行、销毁、白名单与清退'],
    ],
  },
  {
    title: '票务资产模型',
    description: '兼顾机构批量库存与消费者实名持票，并以原子转换连接两种状态。',
    modules: [
      ['BulkTicket', '机构批量库存与票种余额'],
      ['RetailTicket', '消费者实名持票、核销与退款'],
      ['TicketBridge', 'Bulk 与 Retail 之间的原子拆分与合并'],
    ],
  },
  {
    title: '撮合与履约控制',
    description: '覆盖机构撮合、零售供给、退款处置和同实名主体迁移的完整路径。',
    modules: [
      ['BulkExchange', '订单簿、托管、TTL 与禁止自撮合'],
      ['BulkFixedPrice', '一口价批销、即时结算与应急撤销'],
      ['ConsumerRetail', '做市供给、原子交付与来源留痕'],
      ['ConsumerRefund', '退款审核、销毁、扣款与应急付款'],
      ['AccountMigration', '申请排期、多签审批与受控迁移'],
    ],
  },
] as const;

const stakeholderValues = [
  { title: '景区与发行方', description: '统一票种、库存、渠道和履约状态，减少超售、假票与跨渠道对账成本。' },
  { title: '机构市场', description: '在准入、托管、定价和合约约束下完成批量权益的撮合与结算。' },
  { title: '做市与分销机构', description: '基于可信票源管理库存、报价和零售供给，并保留完整业务证据。' },
  { title: '银行与服务机构', description: '依据明确的业务状态承接凭证、清分、退款和对账协同。' },
  { title: '监管与审计机构', description: '穿透查看主体、权限、合约调用、资产状态和异常处置记录。' },
  { title: '消费者', description: '清晰掌握权益来源、实名规则、预约核销、退款和授权流通条件。' },
] as const;

const platformAdvantages = [
  { number: '01', title: '规则先行', description: '发行、准入、撮合、退款与迁移规则均由明确权限和状态约束。' },
  { number: '02', title: '多方可信', description: '参与机构共享可验证账本，关键业务结果不依赖单一数据库说明。' },
  { number: '03', title: '撮合可控', description: '订单簿、一口价和零售路径都具备托管、回退、暂停与审计机制。' },
  { number: '04', title: '全程留痕', description: '从发行到现实履约关联调用人、业务状态、合约事件和验收证据。' },
] as const;

interface SectionIntroProps {
  eyebrow: string;
  title: string;
  description: string;
  centered?: boolean;
}

const SectionIntro: React.FC<SectionIntroProps> = ({ eyebrow, title, description, centered = false }) => (
  <Stack
    spacing={1.5}
    alignItems={centered ? 'center' : 'flex-start'}
    textAlign={centered ? 'center' : 'left'}
    sx={{ mb: { xs: 4, md: 5.5 } }}
  >
    <Typography sx={{ color: Brand.blue, fontSize: 11, fontWeight: 850, letterSpacing: 1.7 }}>
      {eyebrow}
    </Typography>
    <Typography
      component="h2"
      sx={{
        maxWidth: 820,
        fontSize: 'clamp(1.9rem, 4vw, 3.1rem)',
        fontWeight: 760,
        letterSpacing: '-.045em',
        lineHeight: 1.15,
      }}
    >
      {title}
    </Typography>
    <Typography color="text.secondary" sx={{ maxWidth: 760, fontSize: { xs: 14.5, md: 16 }, lineHeight: 1.85 }}>
      {description}
    </Typography>
  </Stack>
);

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { mode, toggleTheme } = useThemeMode();
  const theme = useTheme();
  const isDark = mode === 'dark';
  const sectionAltBg = isDark
    ? alpha(theme.palette.common.white, 0.028)
    : alpha(theme.palette.common.black, 0.018);
  const panelBg = isDark ? alpha('#17233B', 0.74) : alpha('#FFFFFF', 0.9);
  const lineColor = isDark ? 'rgba(148,163,184,.17)' : 'rgba(16,35,63,.09)';

  React.useEffect(() => {
    const previousMargin = document.body.style.margin;
    document.body.style.margin = '0';
    return () => {
      document.body.style.margin = previousMargin;
    };
  }, []);

  const scrollTo = (href: string) => {
    const element = document.querySelector(href);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  const openSystemIntroduction = () => {
    window.location.href = '/system-introduction/';
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        color: 'text.primary',
        '& *': { boxSizing: 'border-box' },
      }}
    >
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: alpha(theme.palette.background.paper, 0.84),
          color: 'text.primary',
          backdropFilter: 'blur(18px) saturate(1.2)',
          borderBottom: `1px solid ${lineColor}`,
        }}
      >
        <Container maxWidth={false} sx={{ maxWidth: 1320 }}>
          <Stack direction="row" alignItems="center" sx={{ minHeight: { xs: 64, md: 70 } }}>
            <Box onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} sx={{ cursor: 'pointer', flexShrink: 0 }}>
              <RtaDexLogo size="small" animated />
            </Box>
            <Stack
              direction="row"
              spacing={{ lg: 3, xl: 3.5 }}
              sx={{ display: { xs: 'none', lg: 'flex' }, ml: 'auto', mr: 3.5 }}
            >
              {navLinks.map((item) => (
                <Typography
                  key={item.href}
                  onClick={() => scrollTo(item.href)}
                  sx={{
                    color: 'text.secondary',
                    cursor: 'pointer',
                    fontSize: 12.5,
                    fontWeight: 650,
                    whiteSpace: 'nowrap',
                    transition: 'color .2s ease',
                    '&:hover': { color: Brand.blue },
                  }}
                >
                  {item.label}
                </Typography>
              ))}
            </Stack>
            <Box sx={{ flexGrow: { xs: 1, lg: 0 } }} />
            <Button
              size="small"
              variant="text"
              onClick={openSystemIntroduction}
              sx={{ display: { xs: 'none', sm: 'inline-flex' }, mr: 1, color: 'text.secondary', whiteSpace: 'nowrap' }}
            >
              完整说明
            </Button>
            <Button size="small" variant="contained" onClick={() => navigate('/entry')} sx={{ px: { xs: 1.5, sm: 2.2 }, whiteSpace: 'nowrap' }}>
              进入撮合系统
            </Button>
            <IconButton aria-label="切换深浅色模式" onClick={toggleTheme} size="small" sx={{ ml: .75, color: 'text.secondary' }}>
              {isDark ? <LightModeIcon sx={{ fontSize: 19 }} /> : <DarkModeIcon sx={{ fontSize: 19 }} />}
            </IconButton>
          </Stack>
        </Container>
      </AppBar>

      <Box
        id="hero"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          pt: { xs: 7, md: 10 },
          pb: { xs: 6, md: 9 },
          background: isDark
            ? 'radial-gradient(circle at 76% 18%, rgba(14,165,233,.14), transparent 30%), linear-gradient(180deg,#0F172A 0%,#111C31 100%)'
            : 'radial-gradient(circle at 76% 18%, rgba(14,165,233,.11), transparent 29%), linear-gradient(180deg,#FCFDFE 0%,#F4F7FB 100%)',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            opacity: isDark ? .2 : .32,
            backgroundImage: `linear-gradient(${lineColor} 1px, transparent 1px), linear-gradient(90deg, ${lineColor} 1px, transparent 1px)`,
            backgroundSize: '48px 48px',
            maskImage: 'linear-gradient(to bottom, black, transparent 86%)',
          },
        }}
      >
        <Container maxWidth={false} sx={{ maxWidth: 1320, position: 'relative' }}>
          <Grid container spacing={{ xs: 6, md: 7, lg: 9 }} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Box sx={{ px: 1.25, py: .65, borderRadius: 999, bgcolor: alpha(Brand.blue, isDark ? .14 : .08), color: Brand.blue, fontSize: 10.5, fontWeight: 850, letterSpacing: 1.3 }}>
                  SMART CONTRACT MATCHING SYSTEM
                </Box>
                <Box sx={{ px: 1.25, py: .65, borderRadius: 999, bgcolor: alpha(Brand.amber, isDark ? .15 : .08), color: isDark ? '#FBBF24' : '#8A5200', fontSize: 10.5, fontWeight: 800 }}>
                  许可型撮合网络
                </Box>
              </Stack>
              <Typography component="h1" sx={{ mt: 3, fontSize: 'clamp(3rem, 6.8vw, 5.4rem)', fontWeight: 780, letterSpacing: '-.065em', lineHeight: .92 }}>
                RtaDex
              </Typography>
              <Typography component="p" sx={{ mt: 2.5, maxWidth: 680, fontSize: 'clamp(1.65rem, 3.3vw, 2.6rem)', fontWeight: 680, letterSpacing: '-.035em', lineHeight: 1.2 }}>
                面向真实文旅资产场景的
                <Box component="span" sx={{ color: Brand.blue }}>智能合约撮合系统</Box>
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 2.75, maxWidth: 650, fontSize: { xs: 15, md: 16.5 }, lineHeight: 1.9 }}>
                构建于 TIBCOS 智旅链之上，面向数字门票、酒店间夜与体验权益，提供身份准入、可信发行、合约撮合、结算协同、履约管理和全链路审计能力。
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1.25, maxWidth: 650, fontSize: 12.5, lineHeight: 1.75 }}>
                平台聚焦文旅权益数字化与机构协作，不直接提供投资理财、公众支付或资金托管服务。
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 4.25, alignItems: { xs: 'stretch', sm: 'center' } }}>
                <Button variant="contained" size="large" endIcon={<ArrowForwardIcon />} onClick={() => navigate('/entry')} sx={{ px: 3.2 }}>
                  进入撮合系统
                </Button>
                <Button variant="outlined" size="large" onClick={openSystemIntroduction} sx={{ px: 3.2 }}>
                  查看完整系统说明
                </Button>
              </Stack>
              <Stack direction="row" spacing={{ xs: 1.5, sm: 2.5 }} useFlexGap flexWrap="wrap" sx={{ mt: 3.5 }}>
                {['许可型身份', '确定性合约', '全链路审计'].map((item) => (
                  <Stack key={item} direction="row" spacing={.65} alignItems="center">
                    <CheckIcon sx={{ fontSize: 15, color: Brand.green }} />
                    <Typography color="text.secondary" sx={{ fontSize: 12.5 }}>{item}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                component="img"
                src="/brand/rtadex-logo.png"
                alt="RtaDex 智旅汇品牌图"
                data-testid="home-hero-brand-logo"
                sx={{ display: 'block', width: { xs: '62%', sm: '56%', md: '72%' }, maxWidth: 420, height: 'auto', mx: 'auto', mb: 3 }}
              />
              <Box
                sx={{
                  p: { xs: 2.25, sm: 3 },
                  border: `1px solid ${lineColor}`,
                  borderRadius: 3,
                  bgcolor: panelBg,
                  backdropFilter: 'blur(14px)',
                  boxShadow: isDark ? 'none' : '0 24px 70px rgba(15,23,42,.07)',
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                  <Box>
                    <Typography sx={{ fontSize: 10, color: Brand.blue, fontWeight: 850, letterSpacing: 1.45 }}>VERIFICATION MAP</Typography>
                    <Typography sx={{ mt: .6, fontSize: 17, fontWeight: 760 }}>从业务请求到可验证证据</Typography>
                  </Box>
                  <Box sx={{ px: 1.1, py: .55, borderRadius: 999, bgcolor: alpha(Brand.green, isDark ? .16 : .08), color: Brand.green, fontSize: 10.5, fontWeight: 800 }}>
                    TEST ONLY
                  </Box>
                </Stack>
                <Grid container spacing={1.25} sx={{ mt: 2.5 }}>
                  {[
                    ['01', '身份与角色', '服务端校验'],
                    ['02', '业务与合约', '受控执行'],
                    ['03', '链上与数据', '双向对账'],
                    ['04', '结果与审计', '证据留存'],
                  ].map(([number, title, detail]) => (
                    <Grid size={{ xs: 6 }} key={number}>
                      <Box sx={{ p: 1.6, height: '100%', borderRadius: 2, border: `1px solid ${lineColor}`, bgcolor: alpha(Brand.blue, isDark ? .055 : .025) }}>
                        <Typography sx={{ color: Brand.blue, fontSize: 10, fontWeight: 850 }}>{number}</Typography>
                        <Typography sx={{ mt: 1, fontSize: 13.5, fontWeight: 750 }}>{title}</Typography>
                        <Typography color="text.secondary" sx={{ mt: .45, fontSize: 11.5 }}>{detail}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ bgcolor: 'background.paper', borderTop: `1px solid ${lineColor}`, borderBottom: `1px solid ${lineColor}` }}>
        <Container maxWidth={false} sx={{ maxWidth: 1320 }}>
          <Grid container>
            {trustSignals.map((item, index) => (
              <Grid size={{ xs: 6, md: 3 }} key={item.label}>
                <Box sx={{ px: { xs: 1.5, md: 3 }, py: 2, borderRight: { md: index < 3 ? `1px solid ${lineColor}` : 'none' }, borderBottom: { xs: index < 2 ? `1px solid ${lineColor}` : 'none', md: 'none' } }}>
                  <Typography color="text.secondary" sx={{ fontSize: 10.5, fontWeight: 750, letterSpacing: .7 }}>{item.label}</Typography>
                  <Typography sx={{ mt: .45, fontSize: 13.5, fontWeight: 760 }}>{item.value}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box id="positioning" sx={{ py: { xs: 8, md: 11 }, scrollMarginTop: 72 }}>
        <Container maxWidth={false} sx={{ maxWidth: 1320 }}>
          <SectionIntro
            eyebrow="ONE FOUNDATION · ONE ASSET MODEL · ONE MATCHING SYSTEM"
            title="把品牌关系和系统职责讲清楚"
            description="TIBCOS、RTA 与 RtaDex 分别承担可信协作、资产表达和合约撮合职责。三者协同，但不混淆基础设施、业务资产与撮合系统的边界。"
          />
          <Grid container spacing={2}>
            {positioningLayers.map((item, index) => (
              <Grid size={{ xs: 12, md: 4 }} key={item.acronym}>
                <Box sx={{ p: { xs: 2.5, md: 3.25 }, minHeight: { md: 270 }, height: '100%', borderRadius: 3, border: `1px solid ${lineColor}`, bgcolor: index === 2 ? alpha(Brand.blue, isDark ? .08 : .035) : 'background.paper' }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography sx={{ color: Brand.blue, fontSize: 11, fontWeight: 850, letterSpacing: 1 }}>{item.code}</Typography>
                    <Typography color="text.secondary" sx={{ fontSize: 10.5, fontWeight: 750 }}>{item.caption}</Typography>
                  </Stack>
                  <Typography sx={{ mt: 3.5, color: index === 2 ? Brand.blue : 'text.primary', fontSize: 14, fontWeight: 850, letterSpacing: 1.2 }}>{item.acronym}</Typography>
                  <Typography component="h3" sx={{ mt: 1, fontSize: 21, fontWeight: 760 }}>{item.title}</Typography>
                  <Typography color="text.secondary" sx={{ mt: 1.75, fontSize: 13.5, lineHeight: 1.8 }}>{item.description}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box id="capabilities" sx={{ py: { xs: 8, md: 11 }, bgcolor: sectionAltBg, scrollMarginTop: 72 }}>
        <Container maxWidth={false} sx={{ maxWidth: 1320 }}>
          <SectionIntro
            eyebrow="MATCHING CAPABILITIES"
            title="围绕完整业务闭环组织智能合约撮合"
            description="首页只展示六项核心能力；详细合约、权限矩阵、服务接口和运维状态统一收口到完整系统说明。"
            centered
          />
          <Grid container spacing={2}>
            {capabilities.map((item) => (
              <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={item.title}>
                <Box sx={{ p: 2.75, height: '100%', borderRadius: 3, border: `1px solid ${lineColor}`, bgcolor: 'background.paper', transition: 'transform .2s ease, border-color .2s ease', '&:hover': { transform: 'translateY(-3px)', borderColor: alpha(Brand.blue, .34) } }}>
                  <Box sx={{ width: 42, height: 42, display: 'grid', placeItems: 'center', borderRadius: 2, color: Brand.blue, bgcolor: alpha(Brand.blue, isDark ? .13 : .075), '& svg': { fontSize: 22 } }}>
                    {item.icon}
                  </Box>
                  <Typography component="h3" sx={{ mt: 2.5, fontSize: 17, fontWeight: 760 }}>{item.title}</Typography>
                  <Typography color="text.secondary" sx={{ mt: 1.25, fontSize: 13.5, lineHeight: 1.8 }}>{item.description}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box id="rta-lifecycle" sx={{ py: { xs: 8, md: 11 }, scrollMarginTop: 72 }}>
        <Container maxWidth={false} sx={{ maxWidth: 1320 }}>
          <SectionIntro
            eyebrow="REAL TOURISM ASSET"
            title="把现实文旅资源转化为可验证、可履约的 RTA"
            description="RTA 不是脱离现实服务的虚拟标的，而是把发行主体、权益内容、使用条件、流通边界和履约责任组织为可验证的数据对象。"
          />
          <Grid container spacing={2}>
            {rtaAssetTypes.map((asset) => (
              <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={asset.code}>
                <Box sx={{ p: 2.6, height: '100%', borderRadius: 3, border: `1px solid ${lineColor}`, bgcolor: 'background.paper' }}>
                  <Typography sx={{ color: Brand.blue, fontSize: 10.5, fontWeight: 850, letterSpacing: 1 }}>{asset.code}</Typography>
                  <Typography component="h3" sx={{ mt: 2, fontSize: 17, fontWeight: 760 }}>{asset.title}</Typography>
                  <Typography color="text.secondary" sx={{ mt: 1.2, fontSize: 13, lineHeight: 1.75 }}>{asset.description}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ mt: { xs: 5, md: 7 }, borderTop: `1px solid ${lineColor}`, borderBottom: `1px solid ${lineColor}` }}>
            <Grid container>
              {rtaLifecycle.map((step, index) => (
                <Grid size={{ xs: 12, sm: 6, lg: 2 }} key={step.number}>
                  <Box sx={{ px: { xs: 0, sm: 2.1 }, py: 2.6, minHeight: { lg: 180 }, borderRight: { lg: index < rtaLifecycle.length - 1 ? `1px solid ${lineColor}` : 'none' }, borderBottom: { xs: index < rtaLifecycle.length - 1 ? `1px solid ${lineColor}` : 'none', sm: index < 4 ? `1px solid ${lineColor}` : 'none', lg: 'none' } }}>
                    <Typography sx={{ color: Brand.blue, fontSize: 10.5, fontWeight: 850 }}>{step.number}</Typography>
                    <Typography sx={{ mt: 1.6, fontSize: 15, fontWeight: 760 }}>{step.title}</Typography>
                    <Typography color="text.secondary" sx={{ mt: 1, fontSize: 12.5, lineHeight: 1.7 }}>{step.description}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>

      <Box id="roles" sx={{ py: { xs: 8, md: 11 }, bgcolor: sectionAltBg, scrollMarginTop: 72 }}>
        <Container maxWidth={false} sx={{ maxWidth: 1320 }}>
          <SectionIntro
            eyebrow="NINE ROLE WORKSPACES"
            title="九类角色系统，共用一套身份、撮合与审计边界"
            description="不同参与方看到的是与职责匹配的工作台；真正的权限仍由服务端角色、资源归属和合约规则共同约束。"
            centered
          />
          <Grid container spacing={2}>
            {roleSystems.map((role, index) => (
              <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={role.name}>
                <Box sx={{ p: 2.5, height: '100%', borderRadius: 3, border: `1px solid ${lineColor}`, bgcolor: 'background.paper' }}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
                    <Box>
                      <Typography sx={{ color: Brand.blue, fontSize: 10.5, fontWeight: 850 }}>0{index + 1}</Typography>
                      <Typography component="h3" sx={{ mt: 1.4, fontSize: 17, fontWeight: 760 }}>{role.name}</Typography>
                    </Box>
                    <Box sx={{ px: 1.1, py: .55, borderRadius: 999, bgcolor: alpha(Brand.blue, isDark ? .13 : .07), color: Brand.blue, fontSize: 10.5, fontWeight: 750, whiteSpace: 'nowrap' }}>
                      {role.scope}
                    </Box>
                  </Stack>
                  <Stack spacing={1} sx={{ mt: 2.4 }}>
                    {role.features.map((feature) => (
                      <Stack key={feature} direction="row" spacing={.8} alignItems="center">
                        <CheckIcon sx={{ color: Brand.green, fontSize: 15 }} />
                        <Typography color="text.secondary" sx={{ fontSize: 12.75 }}>{feature}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box id="workflow" sx={{ py: { xs: 8, md: 11 }, scrollMarginTop: 72 }}>
        <Container maxWidth={false} sx={{ maxWidth: 1320 }}>
          <SectionIntro
            eyebrow="MATCHING WORKFLOW"
            title="一条可复验、可回退、可审计的撮合链路"
            description="每一步都有明确输入、执行主体、状态变化和证据输出；不把页面提示、HTTP 200 或单次链上回执当成完整业务成功。"
          />
          <Box sx={{ borderTop: `1px solid ${lineColor}`, borderBottom: `1px solid ${lineColor}` }}>
            <Grid container>
              {workflowSteps.map((step, index) => (
                <Grid size={{ xs: 12, sm: 6, lg: 2 }} key={step.number}>
                  <Box sx={{ px: { xs: 0, sm: 2.25 }, py: 2.75, minHeight: { lg: 185 }, borderRight: { lg: index < workflowSteps.length - 1 ? `1px solid ${lineColor}` : 'none' }, borderBottom: { xs: index < workflowSteps.length - 1 ? `1px solid ${lineColor}` : 'none', sm: index < 4 ? `1px solid ${lineColor}` : 'none', lg: 'none' } }}>
                    <Typography sx={{ color: Brand.blue, fontSize: 10.5, fontWeight: 850, letterSpacing: .8 }}>{step.number}</Typography>
                    <Typography sx={{ mt: 1.75, fontSize: 15, fontWeight: 760 }}>{step.title}</Typography>
                    <Typography color="text.secondary" sx={{ mt: 1, fontSize: 12.5, lineHeight: 1.7 }}>{step.description}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>

      <Box id="architecture" sx={{ py: { xs: 8, md: 11 }, bgcolor: sectionAltBg, scrollMarginTop: 72 }}>
        <Container maxWidth={false} sx={{ maxWidth: 1320 }}>
          <Grid container spacing={{ xs: 5, md: 8 }} alignItems="start">
            <Grid size={{ xs: 12, md: 5 }}>
              <SectionIntro
                eyebrow="SYSTEM ARCHITECTURE"
                title="分层清晰，边界明确"
                description="智能合约、Java 服务、数据库、签名服务、前端应用和运维操作属于不同信任区，各层通过明确接口协作。"
              />
              <Box sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${alpha(Brand.blue, isDark ? .25 : .15)}`, bgcolor: alpha(Brand.blue, isDark ? .085 : .035) }}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <SecurityIcon sx={{ color: Brand.blue }} />
                  <Typography sx={{ fontSize: 14, fontWeight: 760 }}>核心架构原则</Typography>
                </Stack>
                <Typography color="text.secondary" sx={{ mt: 1.5, fontSize: 13, lineHeight: 1.8 }}>
                  服务端权限优先、领域数据独立、链上链下持续对账、写操作可追踪，高风险动作具备确认和回退考虑。
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Stack spacing={1.25}>
                {architectureLayers.map((layer, index) => (
                  <Grid key={layer.label} container spacing={2} alignItems="center" sx={{ p: { xs: 2, md: 2.35 }, borderRadius: 2.5, border: `1px solid ${lineColor}`, bgcolor: 'background.paper' }}>
                    <Grid size={{ xs: 3, sm: 2.2 }}>
                      <Typography sx={{ color: Brand.blue, fontSize: 10.5, fontWeight: 850 }}>{String(index + 1).padStart(2, '0')} · {layer.label}</Typography>
                    </Grid>
                    <Grid size={{ xs: 9, sm: 4 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 760 }}>{layer.value}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 5.8 }}>
                      <Typography color="text.secondary" sx={{ fontSize: 12.5, lineHeight: 1.7 }}>{layer.detail}</Typography>
                    </Grid>
                  </Grid>
                ))}
              </Stack>
            </Grid>
          </Grid>
          <Box sx={{ mt: { xs: 6, md: 8 }, pt: { xs: 5, md: 7 }, borderTop: `1px solid ${lineColor}` }}>
            <SectionIntro
              eyebrow="SMART CONTRACT MODULES"
              title="十一项合约模块组成撮合与履约闭环"
              description="身份、价值凭证、票务资产、撮合、零售、退款和迁移分别承担清晰职责，避免把所有状态塞进一个不可维护的通用合约。"
            />
            <Grid container spacing={2}>
              {contractGroups.map((group) => (
                <Grid size={{ xs: 12, lg: 4 }} key={group.title}>
                  <Box sx={{ p: { xs: 2.5, md: 3 }, height: '100%', borderRadius: 3, border: `1px solid ${lineColor}`, bgcolor: 'background.paper' }}>
                    <Typography component="h3" sx={{ fontSize: 18, fontWeight: 760 }}>{group.title}</Typography>
                    <Typography color="text.secondary" sx={{ mt: 1.2, minHeight: { lg: 68 }, fontSize: 12.75, lineHeight: 1.75 }}>{group.description}</Typography>
                    <Stack spacing={0} sx={{ mt: 2.2, borderTop: `1px solid ${lineColor}` }}>
                      {group.modules.map(([name, detail]) => (
                        <Box key={name} sx={{ py: 1.45, borderBottom: `1px solid ${lineColor}` }}>
                          <Typography sx={{ color: Brand.blue, fontFamily: 'monospace', fontSize: 11.5, fontWeight: 800 }}>{name}</Typography>
                          <Typography color="text.secondary" sx={{ mt: .55, fontSize: 12.25, lineHeight: 1.65 }}>{detail}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>

      <Box id="agents" sx={{ py: { xs: 8, md: 11 }, scrollMarginTop: 72 }}>
        <Container maxWidth={false} sx={{ maxWidth: 1320 }}>
          <Grid container spacing={{ xs: 5, md: 8 }} alignItems="start">
            <Grid size={{ xs: 12, md: 5 }}>
              <SectionIntro
                eyebrow="GOVERNED AGENT WORKFLOW"
                title="智能体是受编排的工作者，不是越权入口"
                description="AiDo、Codex 或 Claude 通过 MCP 工具网关调用业务能力。编排、身份、工具、知识、审计和执行沙箱保持清晰分层。"
              />
              <Button variant="outlined" endIcon={<ArrowForwardIcon />} onClick={openSystemIntroduction}>
                查看 MCP 与智能节点说明
              </Button>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Grid container spacing={2}>
                {agentFlow.map((item) => (
                  <Grid size={{ xs: 12, sm: 6 }} key={item.title}>
                    <Box sx={{ p: 2.5, height: '100%', borderRadius: 3, border: `1px solid ${lineColor}`, bgcolor: 'background.paper' }}>
                      <Box sx={{ color: Brand.blue, '& svg': { fontSize: 23 } }}>{item.icon}</Box>
                      <Typography sx={{ mt: 1.75, fontSize: 15, fontWeight: 760 }}>{item.title}</Typography>
                      <Typography color="text.secondary" sx={{ mt: 1, fontSize: 12.75, lineHeight: 1.75 }}>{item.description}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box id="value" sx={{ py: { xs: 8, md: 11 }, bgcolor: sectionAltBg }}>
        <Container maxWidth={false} sx={{ maxWidth: 1320 }}>
          <SectionIntro
            eyebrow="APPLICATION VALUE"
            title="每个参与方都获得可衡量、可追溯的业务价值"
            description="平台价值不来自概念包装，而来自角色边界、业务效率、状态一致性和审计证据的共同改善。"
            centered
          />
          <Grid container spacing={2}>
            {stakeholderValues.map((item) => (
              <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={item.title}>
                <Box sx={{ p: 2.6, height: '100%', borderRadius: 3, border: `1px solid ${lineColor}`, bgcolor: 'background.paper' }}>
                  <Typography component="h3" sx={{ fontSize: 16, fontWeight: 760 }}>{item.title}</Typography>
                  <Typography color="text.secondary" sx={{ mt: 1.2, fontSize: 13, lineHeight: 1.75 }}>{item.description}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Grid container sx={{ mt: { xs: 5, md: 7 }, borderTop: `1px solid ${lineColor}`, borderBottom: `1px solid ${lineColor}` }}>
            {platformAdvantages.map((item, index) => (
              <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={item.number}>
                <Box sx={{ px: { xs: 0, sm: 2.5 }, py: 2.8, minHeight: { lg: 185 }, borderRight: { lg: index < platformAdvantages.length - 1 ? `1px solid ${lineColor}` : 'none' }, borderBottom: { xs: index < platformAdvantages.length - 1 ? `1px solid ${lineColor}` : 'none', sm: index < 2 ? `1px solid ${lineColor}` : 'none', lg: 'none' } }}>
                  <Typography sx={{ color: Brand.blue, fontSize: 11, fontWeight: 850 }}>{item.number}</Typography>
                  <Typography sx={{ mt: 1.6, fontSize: 16, fontWeight: 760 }}>{item.title}</Typography>
                  <Typography color="text.secondary" sx={{ mt: 1, fontSize: 12.75, lineHeight: 1.75 }}>{item.description}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box id="governance" sx={{ py: { xs: 8, md: 11 }, scrollMarginTop: 72 }}>
        <Container maxWidth={false} sx={{ maxWidth: 1320 }}>
          <SectionIntro
            eyebrow="MATCHING & SECURITY BOUNDARIES"
            title="把系统能做什么，以及不能做什么写清楚"
            description="撮合系统的可信度首先来自清晰边界。身份、资产、执行、验收与对外表述必须保持一致。"
            centered
          />
          <Grid container spacing={2}>
            {governanceItems.map((item) => (
              <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={item.title}>
                <Box sx={{ p: 2.6, height: '100%', borderRadius: 3, border: `1px solid ${lineColor}`, bgcolor: 'background.paper' }}>
                  <Box sx={{ color: Brand.blue, '& svg': { fontSize: 23 } }}>{item.icon}</Box>
                  <Typography sx={{ mt: 1.75, fontSize: 15, fontWeight: 760 }}>{item.title}</Typography>
                  <Typography color="text.secondary" sx={{ mt: 1, fontSize: 12.75, lineHeight: 1.75 }}>{item.description}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 8, md: 10 }, background: isDark ? 'linear-gradient(135deg,#111C31,#152541)' : 'linear-gradient(135deg,#EEF5FF,#F4FBFC)' }}>
        <Container maxWidth="md">
          <Stack alignItems="center" textAlign="center" spacing={2}>
            <Box sx={{ width: 48, height: 48, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: alpha(Brand.blue, isDark ? .15 : .09), color: Brand.blue }}>
              <AgentIcon />
            </Box>
            <Typography component="h2" sx={{ fontSize: 'clamp(1.8rem,4vw,2.8rem)', fontWeight: 760, letterSpacing: '-.04em' }}>
              从系统定位到撮合执行，保持同一套边界
            </Typography>
            <Typography color="text.secondary" sx={{ maxWidth: 680, fontSize: 14.5, lineHeight: 1.85 }}>
              进入撮合系统处理角色业务，或查看完整系统说明了解合约、架构、权限和运行边界。
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 1, width: { xs: '100%', sm: 'auto' } }}>
              <Button variant="contained" size="large" endIcon={<ArrowForwardIcon />} onClick={() => navigate('/entry')} sx={{ px: 3.5 }}>
                进入撮合系统
              </Button>
              <Button variant="outlined" size="large" onClick={openSystemIntroduction} sx={{ px: 3.5 }}>
                完整系统说明
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <CompanyFooter />
    </Box>
  );
};

export default LandingPage;
